/**
 * Auth-related Zod schemas — shared between FE (form validation) and BE
 * (controller input validation).
 *
 * Per DESIGN.md §5 + ARCHITECTURE.md §6.1.
 */
import { z } from 'zod';

/* ─────────────────────────────────────────────────────────────────── */
/* Primitives                                                          */
/* ─────────────────────────────────────────────────────────────────── */

export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Email is required')
  .max(320, 'Email is too long')
  .email('Invalid email format');

export const PasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .refine((s) => /[A-Za-z]/.test(s), 'Password must contain at least one letter')
  .refine((s) => /\d/.test(s), 'Password must contain at least one digit');

export const NameSchema = z
  .string()
  .trim()
  .min(1, 'Required')
  .max(100, 'Too long');

export const TenantNameSchema = z
  .string()
  .trim()
  .min(1, 'Tenant name is required')
  .max(200, 'Tenant name is too long');

/* ─────────────────────────────────────────────────────────────────── */
/* Request schemas                                                     */
/* ─────────────────────────────────────────────────────────────────── */

export const SignupSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  firstName: NameSchema,
  lastName: NameSchema,
  tenantName: TenantNameSchema,
});
export type SignupInput = z.infer<typeof SignupSchema>;

export const LoginSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof LoginSchema>;

export const ForgotPasswordSchema = z.object({
  email: EmailSchema,
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

export const ResetPasswordSchema = z.object({
  token: z.string().min(1),
  newPassword: PasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

export const VerifyEmailSchema = z.object({
  token: z.string().min(1),
});
export type VerifyEmailInput = z.infer<typeof VerifyEmailSchema>;

/* ─────────────────────────────────────────────────────────────────── */
/* Response shapes (informational; controllers map to these manually)  */
/* ─────────────────────────────────────────────────────────────────── */

export const AuthUserDtoSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  roleCode: z.string(),
  emailVerified: z.boolean(),
});
export type AuthUserDto = z.infer<typeof AuthUserDtoSchema>;

export const AuthTenantDtoSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  planTierCode: z.string(),
});
export type AuthTenantDto = z.infer<typeof AuthTenantDtoSchema>;

export const LoginResponseSchema = z.object({
  user: AuthUserDtoSchema,
  /** Null only for the SYSTEM user (no tenant context). */
  tenant: AuthTenantDtoSchema.nullable(),
  accessToken: z.string(),
  idleExpiresAt: z.string().datetime(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
